import React, { useCallback, useMemo } from "react";
import { useAgentsStore } from "../../../../../../../../store/agents.store";
import {
    selectAgentDetail,
    selectAgentDetailDraft,
    selectAllStates,
    selectAllStatesLoading,
    selectHiddenFields,
    selectIsEditMode,
    selectRestrictedFields,
} from "../../../../../../../../store/agentDetail/agentDetailSelectors";
import { isFieldEditable } from "../../../../../../../../store/agentDetail/agentDetail.utils";
import { buildStateSelectOptions } from "../../../../../../../../agents.utils";
import { AgentDetailSectionEnum, IAgentPersonalInformation } from "../../../../../../../../agentDetail.model";
import { AgentDetailFormField } from "../agentDetailFormField/agentDetailFormField.index";
import { AccountStatusRadio } from "../agentDetailFormField/components/agentDetailFormInput/agentDetailFormInput.index";
import { PERSONAL_FORM_FIELDS, PersonalFormFieldConfig } from "./personalPanel.constants";
import styles from "./personalPanel.module.css";

export const PersonalPanel = () => {
    const data = useAgentsStore(selectAgentDetail);
    const draft = useAgentsStore(selectAgentDetailDraft);
    const isEditMode = useAgentsStore(selectIsEditMode);
    const restrictedFields = useAgentsStore(selectRestrictedFields);
    const hiddenFields = useAgentsStore(selectHiddenFields);
    const allStates = useAgentsStore(selectAllStates);
    const allStatesLoading = useAgentsStore(selectAllStatesLoading);
    const stateOptions = useMemo(() => buildStateSelectOptions(allStates), [allStates]);
    const updateDraftField = useAgentsStore((state) => state.updateDraftField);

    const personalInformation = draft?.personalInformation;
    const isActiveEditable = isFieldEditable(
        restrictedFields,
        AgentDetailSectionEnum.PERSONAL_INFORMATION,
        "isActive"
    );

    const handleStatusChange = useCallback(
        (nextIsActive: boolean) => {
            updateDraftField(AgentDetailSectionEnum.PERSONAL_INFORMATION, "isActive", nextIsActive);
        },
        [updateDraftField]
    );

    const isFieldDisabled = useCallback(
        (fieldConfig: PersonalFormFieldConfig) => {
            if (!isEditMode) return true;
            if (
                !isFieldEditable(
                    restrictedFields,
                    AgentDetailSectionEnum.PERSONAL_INFORMATION,
                    fieldConfig.field
                )
            ) {
                return true;
            }
            if (
                fieldConfig.addonField &&
                !isFieldEditable(
                    restrictedFields,
                    AgentDetailSectionEnum.PERSONAL_INFORMATION,
                    fieldConfig.addonField
                )
            ) {
                return true;
            }
            return false;
        },
        [isEditMode, restrictedFields]
    );

    const handleFieldChange = useCallback(
        (field: keyof IAgentPersonalInformation, value: string | null) => {
            updateDraftField(AgentDetailSectionEnum.PERSONAL_INFORMATION, field, value);
        },
        [updateDraftField]
    );

    const isFieldHidden = useCallback(
        (field: keyof IAgentPersonalInformation) =>
            hiddenFields.personalInformation.includes(field),
        [hiddenFields.personalInformation]
    );

    const formFields = useMemo(
        () =>
            PERSONAL_FORM_FIELDS.filter((fieldConfig) => !isFieldHidden(fieldConfig.field)).map(
                (fieldConfig) =>
                    fieldConfig.type === "select"
                        ? { ...fieldConfig, options: stateOptions, loading: allStatesLoading }
                        : fieldConfig
            ),
        [isFieldHidden, stateOptions, allStatesLoading]
    );

    return (
        <div className={styles.formContainer}>
            {!isFieldHidden("isActive") && (
                <div className={styles.fullRowFormItem}>
                    <div className={styles.label}>Account Status</div>
                    <div className={styles.value}>
                        <AccountStatusRadio
                            isActive={personalInformation?.isActive ?? false}
                            disabled={!isEditMode || !isActiveEditable}
                            onChange={handleStatusChange}
                        />
                    </div>
                </div>
            )}
            <div className={styles.formGrid}>
                {formFields.map((fieldConfig) => {
                    const disabled = isFieldDisabled(fieldConfig);
                    return (
                        <AgentDetailFormField
                            key={fieldConfig.field}
                            config={fieldConfig}
                            value={(personalInformation?.[fieldConfig.field] as string | null) ?? null}
                            originalValue={
                                (data?.personalInformation?.[fieldConfig.field] as string | null) ?? null
                            }
                            disabled={disabled}
                            addonField={
                                fieldConfig.addonField
                                    ? (personalInformation?.[fieldConfig.addonField] as string | null)
                                    : undefined
                            }
                            onChange={
                                disabled
                                    ? undefined
                                    : (value) => handleFieldChange(fieldConfig.field, value)
                            }
                        />
                    );
                })}
            </div>
        </div>
    );
};
